import { US_PHONE_PATTERN } from 'constants/phone';

const US_LOCAL_DIGITS_PATTERN = /^(\d{3})(\d{3})(\d{4})$/;
const US_COUNTRY_DIGITS_PATTERN = /^1(\d{3})(\d{3})(\d{4})$/;
const EXTENSION_SUFFIX_PATTERN = /\s*(?:extension|ext\.?|x|,|;)\s*\d+\s*$/i;
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

/**
 * Every digit in a value, with punctuation, spaces and country symbols dropped — the shape two
 * differently-formatted renderings of the same number can be compared in.
 * @param {string|number} value
 * @returns {string}
 */
export const getPhoneDigits = value => String(value).replace(/\D/g, '');

// A TEL_EXT value carries its extension inline ("555-123-4567 x89"). Digits-only normalization
// would fold it into the number and dial a different party, so drop the extension segment first.
const stripExtension = value => String(value).trim().replace(EXTENSION_SUFFIX_PATTERN, '').trim();

/**
 * Canonical display format for a US phone number, whatever shape it arrives in
 * (bare digits, `+1…`, or already punctuated): a 10-digit value renders as
 * "(234) 567-8901" and an 11-digit country-coded one as "+1 (234) 567-8901".
 * Anything that isn't US-shaped — short, international, free text — is returned
 * untouched so unknown formats are never mangled.
 * @param {string|number} phoneNumber
 * @returns {string}
 */
export const formatPhoneNumberForDisplay = phoneNumber => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  const source = String(phoneNumber).trim();
  const digits = getPhoneDigits(source);

  const localMatch = digits.match(US_LOCAL_DIGITS_PATTERN);

  if (localMatch) {
    return `(${localMatch[1]}) ${localMatch[2]}-${localMatch[3]}`;
  }

  const countryMatch = digits.match(US_COUNTRY_DIGITS_PATTERN);

  if (countryMatch) {
    return `+1 (${countryMatch[1]}) ${countryMatch[2]}-${countryMatch[3]}`;
  }

  return source;
};

/**
 * Strip display formatting down to something the softphone can dial: US-shaped values become
 * E.164 (`+1XXXXXXXXXX`) and an already-E.164 international value keeps its `+`. Any inline
 * extension is dropped first. Fails closed — a value of no recognized dialable shape returns
 * an empty string rather than a digit run that would place a call to an unintended party.
 * @param {string|number} value
 * @returns {string}
 */
export const toDialableNumber = value => {
  if (!value) {
    return '';
  }

  const source = stripExtension(value);
  const digits = getPhoneDigits(source);

  if (US_LOCAL_DIGITS_PATTERN.test(digits)) {
    return `+1${digits}`;
  }

  if (US_COUNTRY_DIGITS_PATTERN.test(digits)) {
    return `+${digits}`;
  }

  const internationalNumber = source.startsWith('+') ? `+${digits}` : '';

  return E164_PATTERN.test(internationalNumber) ? internationalNumber : '';
};

export const normalizePhoneInput = value => {
  if (!value?.includes('+')) {
    return null;
  }

  const match = US_PHONE_PATTERN.exec(value);

  return match ? `${match[1]}${match[2]}${match[3]}` : '';
};

export const onPhoneFieldInput = e => {
  const result = normalizePhoneInput(e.target.value);

  if (result !== null) {
    e.target.value = result;
  }
};
